// captureBin.js — pure helpers for the Capture Log "Recently Deleted" soft-delete bin.
//
// Delete MOVES a row out of the live captureLog into a job-level `captureLogDeleted[]`
// array. That array lives on the ROOT job doc (NOT a session-scoped field), so on
// multi-day jobs every day's deletions pool into one job-wide list you can search in
// one place — while each entry remembers which day it came from (`deletedFromSessionId`)
// so Restore can route it back to the correct session. Nothing is erased until an admin
// empties the bin (no auto-expiry — Ivin's call, 2026-05-31).
//
// These are deliberately framework-free so they can be unit-tested and reused for a
// generic "recently deleted" pattern elsewhere in the app.

// Bin-only metadata stamped on a row when it is soft-deleted; stripped again on restore
// so a restored row is byte-identical to what it was before deletion.
export const BIN_META_KEYS = ["deletedFromSessionId", "deletedAt", "deletedBy"];

// Tag a live captureLog row as deleted. sessionId is the day it came from (null for
// single-day jobs / root-level rows). `at` is a millisecond timestamp; `by` is a label.
export function stampDeleted(row, { sessionId = null, at, by } = {}) {
  return {
    ...row,
    deletedFromSessionId: sessionId ?? null,
    deletedAt: at ?? null,
    deletedBy: by || "",
  };
}

// Remove the bin metadata so the row goes back into the live log exactly as it was.
export function stripBinMeta(row) {
  const clean = { ...row };
  BIN_META_KEYS.forEach((k) => delete clean[k]);
  return clean;
}

// Short, dependency-free relative time for the bin list ("just now", "5m ago",
// "3h ago", "2d ago"). `now` is passed in (testable; no hidden clock).
export function relativeTime(ts, now) {
  if (!ts || !now) return "";
  const diff = Math.max(0, now - ts);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
